// Read-only (or HR-edit-toggled) rendering of the same 7 sections the
// employee's own "Application for Intimation of Resignation" form
// (IntimationResignation.tsx) shows — same labels, same field order, same
// values — reused by both ApprovalDetail.tsx (mode="view", every approver
// including the Competent Authority) and HRVerificationDetail.tsx
// (mode="view" | "edit", all 4 HR levels). IntimationResignation.tsx itself
// is not touched; this is a fresh, display-oriented implementation matching
// its labels/order/conditional-visibility rules exactly.
//
// Three kinds of fields, by data source:
//  - Persisted on the esep_request row itself (`d`) — editable via the
//    existing hrUpdateResignationRequest() DTO (HrUpdateRequestFields).
//  - Persisted on esep_request but NOT in that DTO (e.g. customer_specific_
//    status, short_notice_amount, the CO/Dy.HOD/HOD/Director hierarchy names)
//    — always read-only here too, since HR has no backend path to edit them.
//  - Never persisted onto esep_request at all (Place of Posting, Basic Pay,
//    Home Address) — these only ever exist on the live SAP master-data
//    lookup (EmployeeDetails, the same getEmployeeDetails() call the
//    employee's own form uses) — sourced from the `empMaster` prop, always
//    read-only, exactly as the employee form itself treats them.
import React from "react";
import {
  RadioGroup,
  OutstandingLoansSection,
  BondSelectionsSection,
  WarningBanner,
  DocumentList,
  EmployeeSearchInput,
} from "./components";
import {
  formatINR,
  formatDate,
  EmployeeDetails,
  EmployeeSearchResult,
  DECLARED_LOAN_TYPES,
} from "../services/eSeparation.service";

export type SectionMode = "view" | "edit";

const fmt = (v: any): string => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) return formatDate(v);
  return String(v);
};

const toDateInput = (v: any): string => {
  if (!v) return "";
  const s = String(v);
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : "";
};

// The employee-submission path and the HR-update DTO have historically
// stored this family of flags as either 'Y'/'N' or a raw JS boolean —
// accept either reading, but always write back the canonical 'Y'/'N' the
// backend DTO validates against.
const isYes = (v: any): boolean =>
  v === "Y" || v === true || v === "true" || v === "Yes" || v === "yes";
const yn = (v: any): string => (isYes(v) ? "Yes" : "No");

const SEPARATION_TYPE_LABELS: Record<string, string> = {
  RESIGNATION: "Intimation / Resignation",
  INTIMATION: "Intimation / Resignation",
  DEPUTATION: "Deputation",
  SUPERANNUATION: "Superannuation",
};
const separationTypeLabel = (v: any): string => {
  const key = String(v ?? "").trim().toUpperCase();
  return SEPARATION_TYPE_LABELS[key] || fmt(v);
};

export interface ResignationSectionProps {
  /** The flattened resignation record (same object ApprovalDetail.tsx / HRVerificationDetail.tsx already fetch via getResignationDetails()). */
  d: any;
  mode: SectionMode;
  /** HR's edit-buffer state (only used when mode === "edit"). */
  form?: Record<string, any>;
  setField?: (key: string, value: any) => void;
  setBondFieldValue?: (selIdx: number, fieldKey: string, value: any) => void;
  setDeputationRow?: (idx: number, key: "organization_name" | "duration", value: string) => void;
  addDeputationRow?: () => void;
  removeDeputationRow?: (idx: number) => void;
  /** Find-or-create the esep_loan row matching (loan_type, reference_number)
   * in form.loans and set its amount (and description, for "Others"). */
  setLoanValue?: (
    loan_type: string,
    referenceNumber: string | null,
    amount: string,
    description?: string,
  ) => void;
  setLoanSuretyRow?: (idx: number, key: "loan_type" | "surety_emp_id", value: string) => void;
  addLoanSurety?: () => void;
  removeLoanSurety?: (idx: number) => void;
  /** Live SAP master-data lookup (getEmployeeDetails) — the only source for
   * fields never persisted onto the resignation record itself. */
  empMaster?: EmployeeDetails | null;
}

/* ─────────────────────────── shared building blocks ─────────────────────────── */

const SectionShell: React.FC<{
  number: number;
  title: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}> = ({ number, title, right, children }) => (
  <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
    <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
      <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
        <span
          className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
          style={{ width: 34, height: 34, fontSize: 15 }}
        >
          {String(number).padStart(2, "0")}
        </span>
        {title}
      </h5>
      {right}
    </div>
    <div className="card-body p-4">{children}</div>
  </div>
);

const TextField: React.FC<{
  label: string;
  value: any;
  colClass?: string;
  mode: SectionMode;
  editable?: boolean;
  editValue?: any;
  onChange?: (v: string) => void;
  type?: string;
  helper?: string;
  danger?: boolean;
}> = ({
  label,
  value,
  colClass = "col-lg-3 col-md-4 col-sm-6",
  mode,
  editable = true,
  editValue,
  onChange,
  type = "text",
  helper,
  danger,
}) => (
  <div className={colClass}>
    <label className="form-label fw-semibold text-secondary small mb-1">{label}</label>
    {mode === "edit" && editable && onChange ? (
      <input
        type={type}
        className="form-control"
        value={type === "date" ? toDateInput(editValue ?? value) : editValue ?? ""}
        onChange={(e) => onChange(e.target.value)}
      />
    ) : (
      <input
        type="text"
        className={`form-control bg-light${danger ? " fw-bold text-danger" : ""}`}
        value={fmt(value)}
        readOnly
      />
    )}
    {helper && <small className="text-secondary fw-medium d-block mt-1">{helper}</small>}
  </div>
);

const YesNoField: React.FC<{
  label: string;
  value: any;
  mode: SectionMode;
  onChange?: (v: "Y" | "N") => void;
  colClass?: string;
  required?: boolean;
}> = ({ label, value, mode, onChange, colClass = "col-md-6", required }) => (
  <div className={colClass}>
    <label className="form-label fw-semibold text-secondary small mb-2">
      {label} {required && <span className="text-danger">*</span>}
    </label>
    {mode === "edit" && onChange ? (
      <RadioGroup value={isYes(value) ? "yes" : "no"} onChange={(v) => onChange(v === "yes" ? "Y" : "N")} />
    ) : (
      <div>
        <span className={`badge ${isYes(value) ? "bg-success-transparent text-success" : "bg-light text-dark border"}`}>
          {yn(value)}
        </span>
      </div>
    )}
  </div>
);

const TextAreaField: React.FC<{
  label: string;
  value: any;
  mode: SectionMode;
  onChange?: (v: string) => void;
  rows?: number;
  required?: boolean;
}> = ({ label, value, mode, onChange, rows = 3, required }) => (
  <div className="col-12">
    <label className="form-label fw-semibold text-secondary small mb-1">
      {label} {required && <span className="text-danger">*</span>}
    </label>
    {mode === "edit" && onChange ? (
      <textarea className="form-control" rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
    ) : (
      <div className="p-3 rounded-3 bg-light border">
        <div className="text-dark">{fmt(value)}</div>
      </div>
    )}
  </div>
);

const HierarchyCard: React.FC<{
  level: number;
  title: string;
  name: any;
  empNo: any;
  designation: any;
  department: any;
  mobile?: any;
}> = ({ level, title, name, empNo, designation, department, mobile }) => (
  <div className="col-lg-6 col-12">
    <div className="p-3 bg-light rounded-3 border h-100">
      <div className="d-flex align-items-center gap-2 mb-2 pb-2 border-bottom">
        <span
          className="badge bg-primary text-white rounded-pill shadow-sm"
          style={{ fontSize: "11px", fontWeight: 700, padding: "2px 8px" }}
        >
          Level {level}
        </span>
        <span style={{ fontSize: "13px", fontWeight: 600, color: "#212529" }}>{title}</span>
      </div>
      <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
        <span style={{ fontSize: "13.5px", fontWeight: 700, color: "#1a1f36", lineHeight: 1.3 }}>
          {fmt(name)}
        </span>
        <span
          className="badge font-monospace shadow-sm"
          style={{
            fontSize: "11px",
            fontWeight: 700,
            padding: "2px 6px",
            color: "#0d6efd",
            backgroundColor: "#f0f7ff",
            border: "1px solid #b6d4fe",
            borderRadius: "4px",
          }}
        >
          Emp No: {fmt(empNo)}
        </span>
      </div>
      <div style={{ fontSize: "12px", fontWeight: 500, color: "#6c757d" }}>
        {fmt(designation)} &bull; {fmt(department)}
      </div>
      {mobile !== undefined && (
        <div className="mt-1" style={{ fontSize: "12px", fontWeight: 500, color: "#6c757d" }}>
          Mobile: {fmt(mobile)}
        </div>
      )}
    </div>
  </div>
);

/* ───────────────────────────── SECTION 1 ───────────────────────────── */

export const SubjectEmployeeDetailsSection: React.FC<ResignationSectionProps & { number?: number }> = ({
  d,
  mode,
  form,
  setField,
  empMaster,
  number = 1,
}) => (
  <SectionShell number={number} title="Subject Employee Details">
    <WarningBanner type="info" message="These details are auto-filled from SAP HR Master Data." />
    <div className="row g-3">
      <TextField
        label="What are you applying for?"
        value={separationTypeLabel(d.separation_type)}
        mode={mode}
        editValue={form?.separation_type}
        onChange={setField ? (v) => setField("separation_type", v) : undefined}
      />
      <TextField
        label="Employee Name"
        value={d.emp_name}
        mode={mode}
        editValue={form?.emp_name}
        onChange={setField ? (v) => setField("emp_name", v) : undefined}
      />
      <TextField label="Employee No." value={d.emp_no} mode={mode} editable={false} />
      <TextField
        label="Designation"
        value={d.designation}
        mode={mode}
        editValue={form?.designation}
        onChange={setField ? (v) => setField("designation", v) : undefined}
      />
      <TextField
        label="Regular / Contract"
        value={d.emp_type}
        mode={mode}
        editValue={form?.emp_type}
        onChange={setField ? (v) => setField("emp_type", v) : undefined}
      />
      <TextField
        label="Department"
        value={d.posting}
        mode={mode}
        editValue={form?.posting}
        onChange={setField ? (v) => setField("posting", v) : undefined}
      />
      <TextField label="Place of Posting" value={empMaster?.place_of_posting} mode={mode} editable={false} />
      <TextField label="Wing" value={d.wing} mode={mode} editable={false} />
      <TextField
        label="Pay Scale"
        value={d.pay_scale}
        mode={mode}
        editValue={form?.pay_scale}
        onChange={setField ? (v) => setField("pay_scale", v) : undefined}
      />
      <TextField
        label="Date of Joining"
        value={d.doj}
        mode={mode}
        type="date"
        editValue={form?.doj}
        onChange={setField ? (v) => setField("doj", v) : undefined}
      />
      <TextField label="Customer Specific Status" value={d.customer_specific_status} mode={mode} editable={false} />
      <TextField
        label="Basic Pay"
        value={empMaster?.basic_pay_daily_rate != null ? formatINR(empMaster.basic_pay_daily_rate) : null}
        mode={mode}
        editable={false}
      />
      <TextField
        label="Home Address"
        value={empMaster?.home_address}
        mode={mode}
        editable={false}
        colClass="col-lg-6 col-md-6 col-sm-12"
      />
      {/* Hometown: HR-maintained, not part of the employee's own form —
          kept here since HR already relied on editing it before this
          parity refactor. */}
      <TextField
        label="Hometown"
        value={d.hometown}
        mode={mode}
        editValue={form?.hometown}
        onChange={setField ? (v) => setField("hometown", v) : undefined}
        colClass="col-lg-6 col-md-6 col-sm-12"
      />
    </div>
  </SectionShell>
);

/* ───────────────────────────── SECTION 2 ───────────────────────────── */

export const ApprovalHierarchySection: React.FC<ResignationSectionProps & { number?: number }> = ({
  d,
  mode,
  form,
  setField,
  number = 2,
}) => (
  <SectionShell number={number} title="Approval Hierarchy">
    <WarningBanner
      type="info"
      message="These details are auto-filled from the employee's reporting hierarchy at the time of submission and cannot be edited."
    />
    <div className="row g-3">
      <HierarchyCard
        level={1}
        title="Controlling Officer (CO)"
        name={d.co_name}
        empNo={d.co_emp_no}
        designation={d.co_designation}
        department={d.co_department}
        mobile={d.co_mobile}
      />
      <HierarchyCard
        level={2}
        title="Dy. HOD"
        name={d.dy_hod_name}
        empNo={d.dy_hod_emp_no}
        designation={d.dy_hod_designation}
        department={d.dy_hod_department}
      />
      <HierarchyCard
        level={3}
        title="Head of Department (HOD)"
        name={d.hod_name}
        empNo={d.hod_emp_no}
        designation={d.hod_designation}
        department={d.hod_department}
      />
      {d.director_name && (
        <HierarchyCard
          level={4}
          title="Concerned Director"
          name={d.director_name}
          empNo={d.director_emp_no}
          designation={d.director_designation}
          department={d.director_department}
        />
      )}
    </div>
    {mode === "edit" && setField && (
      <div className="row g-3 mt-1 pt-3 border-top">
        <TextField
          label="Controlling Officer Name (editable)"
          value={d.co_name}
          mode={mode}
          editValue={form?.co_name}
          onChange={(v) => setField("co_name", v)}
          colClass="col-md-6"
        />
        <TextField
          label="Controlling Officer Mobile (editable)"
          value={d.co_mobile}
          mode={mode}
          editValue={form?.co_mobile}
          onChange={(v) => setField("co_mobile", v)}
          colClass="col-md-6"
        />
      </div>
    )}
  </SectionShell>
);

/* ───────────────────────────── SECTION 3 ───────────────────────────── */

export const NoticePeriodSection: React.FC<ResignationSectionProps & { number?: number }> = ({
  d,
  mode,
  form,
  setField,
  number = 3,
}) => (
  <SectionShell number={number} title="Notice Period">
    <WarningBanner type="info" message="Notice period is 89 days as per DMRC policy." />
    <div className="row g-3">
      <TextField
        label="Date of Intimation"
        value={d.intimation_date}
        mode={mode}
        type="date"
        editValue={form?.intimation_date}
        onChange={setField ? (v) => setField("intimation_date", v) : undefined}
        colClass="col-md-6"
      />
      <TextField
        label="Notice End Date"
        value={d.notice_end_date}
        mode={mode}
        type="date"
        editValue={form?.notice_end_date}
        onChange={setField ? (v) => setField("notice_end_date", v) : undefined}
        colClass="col-md-6"
      />
    </div>
  </SectionShell>
);

/* ───────────────────────────── SECTION 4 ───────────────────────────── */

export const EarlyRelievingSection: React.FC<ResignationSectionProps & { number?: number }> = ({
  d,
  mode,
  form,
  setField,
  number = 4,
}) => (
  <SectionShell number={number} title="Early Relieving Request">
    <div className="row g-3">
      <YesNoField
        label="Do you want early relieving?"
        value={d.early_relieve}
        mode={mode}
        onChange={setField ? (v) => setField("early_relieve", v) : undefined}
      />
    </div>
    {isYes(mode === "edit" ? form?.early_relieve ?? d.early_relieve : d.early_relieve) && (
      <div className="p-3 bg-light rounded-3 border mt-3">
        <div className="row g-3">
          <TextField
            label="Desired Relieving Date"
            value={d.desired_relieve_date}
            mode={mode}
            type="date"
            editValue={form?.desired_relieve_date}
            onChange={setField ? (v) => setField("desired_relieve_date", v) : undefined}
            colClass="col-md-4"
          />
          <TextField
            label="Shortfall Days"
            value={d.shortfall_days ? `${d.shortfall_days} days` : "0 days"}
            mode={mode}
            editable={false}
            colClass="col-md-4"
          />
          <TextField
            label="Estimated Short Notice Amount"
            value={d.short_notice_amount ? formatINR(d.short_notice_amount) : null}
            mode={mode}
            editable={false}
            danger
            colClass="col-md-4"
          />
        </div>
      </div>
    )}
  </SectionShell>
);

/* ───────────────────────────── SECTION 5 ───────────────────────────── */

export const BondContactDetailsSection: React.FC<ResignationSectionProps & { number?: number }> = ({
  d,
  mode,
  form,
  setField,
  setBondFieldValue,
  number = 5,
}) => {
  const bondSelections: any[] = mode === "edit" ? form?.bond_selections || [] : d.bond_selections || [];
  return (
    <SectionShell number={number} title="Bond & Contact Details">
      <div className="mb-4 pb-3 border-bottom">
        <div className="fw-bold text-dark mb-2">Selected Bond Types</div>
        {mode === "edit" ? (
          bondSelections.length === 0 ? (
            <p className="text-muted fst-italic mb-0 text-center py-2">No service bonds selected for this request.</p>
          ) : (
            <div className="d-flex flex-column gap-3">
              {bondSelections.map((sel, selIdx) => (
                <div key={sel.bond_type_id ?? selIdx} className="p-3 bg-light rounded-3 border">
                  <div className="fw-semibold mb-2">{sel.bond_type_name}</div>
                  <div className="row g-3">
                    {(sel.field_meta || []).map((f: any) => (
                      <div key={f.field_key} className="col-md-6">
                        <label className="form-label fw-semibold text-secondary small mb-1">{f.field_label}</label>
                        {f.field_type === "BOOLEAN" ? (
                          <select
                            className="form-select"
                            value={sel.values?.[f.field_key] ? "Y" : "N"}
                            onChange={(e) => setBondFieldValue?.(selIdx, f.field_key, e.target.value === "Y")}
                          >
                            <option value="Y">Yes</option>
                            <option value="N">No</option>
                          </select>
                        ) : (
                          <input
                            type={f.field_type === "NUMBER" ? "number" : f.field_type === "DATE" ? "date" : "text"}
                            className="form-control"
                            value={sel.values?.[f.field_key] ?? ""}
                            onChange={(e) => setBondFieldValue?.(selIdx, f.field_key, e.target.value)}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          <BondSelectionsSection selections={d.bond_selections} />
        )}
      </div>

      <div className="row g-3 mb-4 pb-3 border-bottom">
        <YesNoField
          label="Have you undergone any Foreign Training and signed the bond?"
          value={d.foreign_training_bond}
          mode={mode}
          onChange={setField ? (v) => setField("foreign_training_bond", v) : undefined}
        />
        {isYes(mode === "edit" ? form?.foreign_training_bond ?? d.foreign_training_bond : d.foreign_training_bond) && (
          <>
            <TextField
              label="(i) Period of Bond Executed"
              value={d.foreign_training_period}
              mode={mode}
              editValue={form?.foreign_training_period}
              onChange={setField ? (v) => setField("foreign_training_period", v) : undefined}
              colClass="col-md-6"
            />
            <TextField
              label="Amount of Bond Executed"
              value={d.foreign_training_bond_amount != null ? formatINR(d.foreign_training_bond_amount) : null}
              mode={mode}
              type="number"
              editValue={form?.foreign_training_bond_amount}
              onChange={setField ? (v) => setField("foreign_training_bond_amount", Number(v) || 0) : undefined}
              colClass="col-md-6"
            />
            <TextField
              label="(ii) Details of Training"
              value={d.foreign_training_details}
              mode={mode}
              editValue={form?.foreign_training_details}
              onChange={setField ? (v) => setField("foreign_training_details", v) : undefined}
              colClass="col-md-12"
            />
          </>
        )}
      </div>

      <div className="row g-4 mb-4 pb-3 border-bottom">
        <div className="col-12">
          <YesNoField
            label="Training provided by DMRC (Induction/other course, India/Abroad)"
            value={d.training_bond}
            mode={mode}
            onChange={setField ? (v) => setField("training_bond", v) : undefined}
            colClass="col-12"
          />
          {isYes(mode === "edit" ? form?.training_bond ?? d.training_bond : d.training_bond) && (
            <div className="mt-2">
              {mode === "edit" ? (
                <textarea
                  className="form-control"
                  rows={2}
                  placeholder="Training details"
                  value={form?.training_bond_details ?? ""}
                  onChange={(e) => setField?.("training_bond_details", e.target.value)}
                />
              ) : (
                <div className="p-3 rounded-3 bg-light border">
                  <div className="text-dark">{fmt(d.training_bond_details)}</div>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="col-12">
          <YesNoField
            label="Any other Bond submitted to DMRC (other than Joining Bond)"
            value={d.other_bond_submitted}
            mode={mode}
            onChange={setField ? (v) => setField("other_bond_submitted", v) : undefined}
            colClass="col-12"
          />
          {isYes(mode === "edit" ? form?.other_bond_submitted ?? d.other_bond_submitted : d.other_bond_submitted) && (
            <div className="mt-2">
              {mode === "edit" ? (
                <textarea
                  className="form-control"
                  rows={2}
                  placeholder="Other bond details"
                  value={form?.other_bond_details ?? ""}
                  onChange={(e) => setField?.("other_bond_details", e.target.value)}
                />
              ) : (
                <div className="p-3 rounded-3 bg-light border">
                  <div className="text-dark">{fmt(d.other_bond_details)}</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div>
        <h6 className="fw-bold text-dark mb-3">Contact Information</h6>
        <div className="row g-3">
          <TextField
            label="Mobile Number"
            value={d.mobile}
            mode={mode}
            editValue={form?.mobile}
            onChange={setField ? (v) => setField("mobile", v) : undefined}
            colClass="col-md-4"
          />
          <TextField
            label="Email Address"
            value={d.email}
            mode={mode}
            type="email"
            editValue={form?.email}
            onChange={setField ? (v) => setField("email", v) : undefined}
            colClass="col-md-4"
          />
          <TextField
            label="Correspondence / Present Address"
            value={d.temp_address}
            mode={mode}
            editValue={form?.temp_address}
            onChange={setField ? (v) => setField("temp_address", v) : undefined}
            colClass="col-md-4"
          />
        </div>
      </div>
    </SectionShell>
  );
};

/* ───────────────────────────── SECTION 6 ───────────────────────────── */

export const ReasonAttachmentsSection: React.FC<ResignationSectionProps & { number?: number }> = ({
  d,
  mode,
  form,
  setField,
  setLoanValue,
  setLoanSuretyRow,
  addLoanSurety,
  removeLoanSurety,
  number = 6,
}) => (
  <SectionShell number={number} title="Reason for Separation & Supporting Documents">
    <div className="row g-3 mb-4">
      <TextAreaField
        label="Reason for Resignation"
        value={mode === "edit" ? form?.reason : d.reason}
        mode={mode}
        onChange={setField ? (v) => setField("reason", v) : undefined}
        rows={4}
        required
      />
    </div>

    <div className="row g-3 mb-4 pb-3 border-bottom">
      <YesNoField
        label="Whether the NOC for Out Side Job is obtained, If yes, mention Post, Advt.no. and date, attach copy of NOC also"
        value={d.noc_attached}
        mode={mode}
        onChange={setField ? (v) => setField("noc_attached", v) : undefined}
        colClass="col-12"
      />
      {isYes(mode === "edit" ? form?.noc_attached ?? d.noc_attached : d.noc_attached) && (
        <>
          <TextField
            label="Post Applied For"
            value={d.noc_post}
            mode={mode}
            editValue={form?.noc_post}
            onChange={setField ? (v) => setField("noc_post", v) : undefined}
            colClass="col-md-4"
          />
          <TextField
            label="Advertisement No."
            value={d.noc_advt_no}
            mode={mode}
            editValue={form?.noc_advt_no}
            onChange={setField ? (v) => setField("noc_advt_no", v) : undefined}
            colClass="col-md-4"
          />
          <TextField
            label="Date"
            value={d.noc_date}
            mode={mode}
            type="date"
            editValue={form?.noc_date}
            onChange={setField ? (v) => setField("noc_date", v) : undefined}
            colClass="col-md-4"
          />
        </>
      )}
    </div>

    <div className="mb-4 pb-3 border-bottom">
      <label className="form-label fw-semibold text-secondary small mb-2 d-block">
        Uploaded Certificates / Supporting Documents
      </label>
      <DocumentList documents={d.documents} />
    </div>

    <div className="mb-4 pb-3 border-bottom">
      <TextField
        label="Educational Qualification in service record (In case higher education obtained after joining, attach NOC/addition order)"
        value={d.qualification_details}
        mode={mode}
        editValue={form?.qualification_details}
        onChange={setField ? (v) => setField("qualification_details", v) : undefined}
        colClass="col-md-6"
      />
    </div>

    <LoansAndSuretiesBlock
      d={d}
      mode={mode}
      form={form}
      setLoanValue={setLoanValue}
      setLoanSuretyRow={setLoanSuretyRow}
      addLoanSurety={addLoanSurety}
      removeLoanSurety={removeLoanSurety}
    />
  </SectionShell>
);

// "Outstanding Loans" + "Indicate Loan or Advances" (field 25) +
// "Your Sureties for Loans" (field 26) — read-only for approvers; editable
// for HR (mode="edit"), against the esep_loan/esep_loan_surety rows already
// on `d.loans`/`d.loan_sureties`. This is the one part of the form the
// employee's own submission already populates dynamically (not a fixed
// snapshot), so both view and edit modes read live off `d`/`form`.
const LoansAndSuretiesBlock: React.FC<
  Pick<
    ResignationSectionProps,
    "d" | "mode" | "form" | "setLoanValue" | "setLoanSuretyRow" | "addLoanSurety" | "removeLoanSurety"
  >
> = ({ d, mode, form, setLoanValue, setLoanSuretyRow, addLoanSurety, removeLoanSurety }) => {
  const loans: any[] = (mode === "edit" ? form?.loans : d.loans) || [];
  const findLoan = (loan_type: string, referenceNumber: string | null) =>
    loans.find(
      (l) => l.loan_type === loan_type && (l.reference_number ?? null) === referenceNumber,
    );
  const overrides: Record<string, string> = {};
  if (mode === "edit") {
    for (const l of loans) {
      if (l.reference_number) overrides[l.reference_number] = String(l.amount ?? "");
    }
  }
  const sureties: any[] = (mode === "edit" ? form?.loan_sureties : d.loan_sureties) || [];

  return (
    <>
      <div className="mb-4 pb-3 border-bottom">
        <h6 className="fw-bold text-dark mb-2">Outstanding Loans</h6>
        {mode === "edit" ? (
          (d.outstanding_loans || []).length === 0 ? (
            <p className="text-muted fst-italic mb-0 text-center py-2">No outstanding loans found.</p>
          ) : (
            <div className="d-flex flex-column gap-2">
              {(d.outstanding_loans || []).map((l: any, i: number) => (
                <div
                  key={(l.reference_number || l.loan_code || "") + i}
                  className="d-flex justify-content-between align-items-center p-2 rounded flex-wrap gap-2"
                  style={{ background: "rgba(0,0,0,0.03)" }}
                >
                  <div className="flex-grow-1 me-2">
                    <div className="fw-semibold">{l.loan_name || l.loan_code || "Loan"}</div>
                    {l.reference_number && <small className="text-muted">Ref: {l.reference_number}</small>}
                  </div>
                  <div className="text-end">
                    <div className="fw-semibold" style={{ color: "#C0282C" }}>
                      {formatINR(l.balance)}
                    </div>
                    <small className="text-muted">SAP Balance</small>
                  </div>
                  {l.reference_number && (
                    <div style={{ width: 160 }}>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        placeholder="Declared amount"
                        value={overrides[l.reference_number] ?? ""}
                        onChange={(e) =>
                          setLoanValue?.("SAP_OVERRIDE", l.reference_number, e.target.value)
                        }
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
        ) : (
          <OutstandingLoansSection loans={d.outstanding_loans || []} />
        )}
      </div>

      <div className="mb-4 pb-3 border-bottom">
        <h6 className="fw-bold text-dark mb-3">Indicate Loan or Advances</h6>
        <div className="row g-3">
          {DECLARED_LOAN_TYPES.map((lt) => {
            const rec = findLoan(lt.code, null);
            return (
              <div key={lt.code} className="col-md-4 col-sm-6">
                <label className="form-label fw-semibold text-secondary small mb-1">{lt.label}</label>
                {mode === "edit" ? (
                  <input
                    type="number"
                    min={0}
                    className="form-control"
                    placeholder="Rs."
                    value={rec?.amount ?? ""}
                    onChange={(e) => setLoanValue?.(lt.code, null, e.target.value)}
                  />
                ) : (
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={rec ? formatINR(rec.amount) : "—"}
                    readOnly
                  />
                )}
              </div>
            );
          })}
        </div>
        {(() => {
          const othersRec = findLoan("OTHERS", null);
          return othersRec?.description ? (
            <div className="mt-2">
              <small className="text-secondary fw-medium">Others: {othersRec.description}</small>
            </div>
          ) : null;
        })()}
      </div>

      <div>
        <h6 className="fw-bold text-dark mb-2">Your Sureties for Loans</h6>
        {sureties.length === 0 && mode === "view" && (
          <p className="text-muted fst-italic mb-0">No sureties on record.</p>
        )}
        <div className="d-flex flex-column gap-2">
          {sureties.map((row: any, i: number) =>
            mode === "edit" ? (
              <div key={i} className="row g-2 align-items-start">
                <div className="col-md-4">
                  <label className="form-label fw-semibold text-secondary small mb-1">
                    Surety for Loan / Advance Name
                  </label>
                  <input
                    className="form-control"
                    placeholder="e.g. Multi Purpose Advance"
                    value={row.loan_type}
                    onChange={(e) => setLoanSuretyRow?.(i, "loan_type", e.target.value)}
                  />
                </div>
                <div className="col-md-7">
                  <label className="form-label fw-semibold text-secondary small mb-1">
                    Surety Employee
                  </label>
                  <EmployeeSearchInput
                    value={row.surety_emp_id}
                    placeholder="Type employee number or name…"
                    onChange={(v) => setLoanSuretyRow?.(i, "surety_emp_id", v)}
                    onSelect={(emp: EmployeeSearchResult) =>
                      setLoanSuretyRow?.(i, "surety_emp_id", emp.emp_no)
                    }
                  />
                  {row.surety_emp_name && (
                    <small className="text-muted d-block mt-1">{row.surety_emp_name}</small>
                  )}
                </div>
                <div className="col-md-1 d-flex flex-column">
                  <label className="form-label small mb-1 invisible">Remove</label>
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    onClick={() => removeLoanSurety?.(i)}
                    title="Remove"
                  >
                    <i className="ti ti-trash" />
                  </button>
                </div>
              </div>
            ) : (
              <div key={i} className="p-2 rounded" style={{ background: "rgba(0,0,0,0.03)" }}>
                <span className="fw-semibold">{row.loan_type}</span>
                <span className="text-muted">
                  {" "}
                  — {row.surety_emp_name || "—"} ({row.surety_emp_id})
                </span>
              </div>
            ),
          )}
        </div>
        {mode === "edit" && (
          <button type="button" className="btn btn-outline-secondary btn-sm mt-2" onClick={addLoanSurety}>
            <i className="ti ti-plus me-1" />
            Add Surety
          </button>
        )}
      </div>
    </>
  );
};

/* ───────────────────────────── SECTION 7 ───────────────────────────── */

export const DeclarationsHandoverSection: React.FC<ResignationSectionProps & { number?: number }> = ({
  d,
  mode,
  form,
  setField,
  setDeputationRow,
  addDeputationRow,
  removeDeputationRow,
  empMaster,
  number = 7,
}) => {
  const deputationHistory: any[] =
    mode === "edit" ? form?.deputation_history || [] : d.deputation_history || [];

  return (
    <SectionShell number={number} title="Declarations & Handover Details">
      <div className="row g-3 mb-4 pb-3 border-bottom">
        <YesNoField
          label="Any item like Tools / Computer / Laptop / Library books in your name?"
          value={d.items_in_custody}
          mode={mode}
          onChange={setField ? (v) => setField("items_in_custody", v) : undefined}
        />
        {isYes(mode === "edit" ? form?.items_in_custody ?? d.items_in_custody : d.items_in_custody) && (
          <TextField
            label="Item Name(s)"
            value={d.items_in_custody_text}
            mode={mode}
            editValue={form?.items_in_custody_text}
            onChange={setField ? (v) => setField("items_in_custody_text", v) : undefined}
            colClass="col-md-6"
          />
        )}
      </div>

      <div className="row g-3 mb-4 pb-3 border-bottom">
        <TextField
          label="Nominated officer/employee to take temporary charge from you"
          value={d.temp_charge_officer}
          mode={mode}
          editValue={form?.temp_charge_officer}
          onChange={setField ? (v) => setField("temp_charge_officer", v) : undefined}
          colClass="col-md-6"
        />
      </div>

      {/* Auto-fetched from SAP (sap_lwp_records) — never employee- or
          HR-editable, matching the employee form's own read-only framing.
          CCL is shown only for female employees. */}
      {empMaster?.gender === "2" && (
        <div className="row g-3 mb-4 pb-3 border-bottom">
          <div className="col-12">
            <label className="form-label fw-semibold text-secondary small mb-2 d-block">
              Child Care Leave (CCL) taken in last 2 years?
            </label>
            <div>
              <span
                className={`badge ${isYes(d.ccl_availed) ? "bg-success-transparent text-success" : "bg-light text-dark border"}`}
              >
                {yn(d.ccl_availed)}
              </span>
              {isYes(d.ccl_availed) && d.ccl_period && (
                <div className="small text-dark mt-1">{d.ccl_period}</div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="row g-3 mb-4 pb-3 border-bottom">
        <div className="col-12">
          <label className="form-label fw-semibold text-secondary small mb-2 d-block">
            Leave Without Pay (LWP) taken in last 2 years?
          </label>
          <div>
            <span
              className={`badge ${isYes(d.lwp_availed) ? "bg-success-transparent text-success" : "bg-light text-dark border"}`}
            >
              {yn(d.lwp_availed)}
            </span>
            {isYes(d.lwp_availed) && d.lwp_period && (
              <div className="small text-dark mt-1">{d.lwp_period}</div>
            )}
          </div>
        </div>
      </div>

      <div className="row g-3 mb-4 pb-3 border-bottom">
        <YesNoField
          label="Extraordinary Leave (EOL) availed?"
          value={d.eol_availed}
          mode={mode}
          onChange={setField ? (v) => setField("eol_availed", v) : undefined}
        />
        {isYes(mode === "edit" ? form?.eol_availed ?? d.eol_availed : d.eol_availed) && (
          <div className="col-12">
            <label className="form-label fw-semibold text-secondary small mb-1">Duration &amp; Exact Dates</label>
            {mode === "edit" ? (
              <textarea
                className="form-control"
                rows={2}
                placeholder="e.g. 01.01.2025 to 15.01.2025, 01.06.2025 to 10.06.2025"
                value={form?.eol_details ?? ""}
                onChange={(e) => setField?.("eol_details", e.target.value)}
              />
            ) : (
              <div className="p-3 rounded-3 bg-light border">
                <div className="text-dark">{fmt(d.eol_details)}</div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="row g-3 mb-4 pb-3 border-bottom">
        <YesNoField
          label="Have you sought and withdrawn your resignation in last 12 months?"
          value={d.withdraw_last_yr}
          mode={mode}
          onChange={setField ? (v) => setField("withdraw_last_yr", v) : undefined}
        />
        {isYes(mode === "edit" ? form?.withdraw_last_yr ?? d.withdraw_last_yr : d.withdraw_last_yr) && (
          <TextField
            label="Details"
            value={d.withdraw_last_yr_details}
            mode={mode}
            editValue={form?.withdraw_last_yr_details}
            onChange={setField ? (v) => setField("withdraw_last_yr_details", v) : undefined}
            colClass="col-md-6"
          />
        )}
      </div>

      <div className="row g-3 mb-4 pb-3 border-bottom">
        <YesNoField
          label="Have you ever been on deputation?"
          value={d.deputation_availed}
          mode={mode}
          onChange={setField ? (v) => setField("deputation_availed", v) : undefined}
        />
        {isYes(mode === "edit" ? form?.deputation_availed ?? d.deputation_availed : d.deputation_availed) && (
          <>
            <YesNoField
              label="Have all remittances from deputation service been received in DMRC?"
              value={d.deputation_remittance_received}
              mode={mode}
              onChange={setField ? (v) => setField("deputation_remittance_received", v) : undefined}
            />
            <div className="col-12">
              <label className="form-label fw-semibold text-secondary small mb-2">
                Deputation Period(s), Place(s) &amp; Organization
              </label>
              {deputationHistory.length === 0 && mode === "view" && (
                <p className="text-muted fst-italic mb-0">No deputation history on record.</p>
              )}
              {deputationHistory.map((row: any, i: number) =>
                mode === "edit" ? (
                  <div className="row g-2 mb-2" key={i}>
                    <div className="col-md-6">
                      <input
                        className="form-control"
                        placeholder="Organization name"
                        value={row.organization_name ?? ""}
                        onChange={(e) => setDeputationRow?.(i, "organization_name", e.target.value)}
                      />
                    </div>
                    <div className="col-md-5">
                      <input
                        className="form-control"
                        placeholder="Duration (e.g. Jan 2020 – Dec 2022)"
                        value={row.duration ?? ""}
                        onChange={(e) => setDeputationRow?.(i, "duration", e.target.value)}
                      />
                    </div>
                    <div className="col-md-1 d-flex align-items-center">
                      {deputationHistory.length > 1 && (
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => removeDeputationRow?.(i)}
                        >
                          <i className="ti ti-trash" />
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div key={i} className="p-2 rounded mb-2" style={{ background: "rgba(0,0,0,0.03)" }}>
                    <span className="fw-semibold">{fmt(row.organization_name)}</span>
                    {row.duration && <span className="text-muted"> &bull; {row.duration}</span>}
                  </div>
                )
              )}
              {mode === "edit" && (
                <button type="button" className="btn btn-outline-secondary btn-sm mt-1" onClick={addDeputationRow}>
                  <i className="ti ti-plus me-1" />
                  Add Another Deputation
                </button>
              )}
            </div>
          </>
        )}
      </div>

      <div className="row g-3 mb-4 pb-3 border-bottom">
        <TextAreaField
          label="Furnish details of any Court Case, Vigilance case and D&AR proceedings pending against you"
          value={mode === "edit" ? form?.pending_cases_details : d.pending_cases_details}
          mode={mode}
          onChange={setField ? (v) => setField("pending_cases_details", v) : undefined}
          rows={2}
        />
      </div>

      <div className="row g-3">
        <TextField
          label="Release/Transfer of Gratuity and Leave Encashment"
          value={isYes(d.gratuity_transfer) ? "Transfer" : "Release"}
          mode={mode}
          editable={false}
          colClass="col-md-6"
        />
        {mode === "edit" && setField && (
          <div className="col-md-6">
            <label className="form-label fw-semibold text-secondary small mb-1">
              Release/Transfer of Gratuity and Leave Encashment (editable)
            </label>
            <select
              className="form-select"
              value={isYes(form?.gratuity_transfer ?? d.gratuity_transfer) ? "Y" : "N"}
              onChange={(e) => setField("gratuity_transfer", e.target.value)}
            >
              <option value="N">Release</option>
              <option value="Y">Transfer</option>
            </select>
          </div>
        )}
      </div>
    </SectionShell>
  );
};

/* ─────────────────────── convenience: render all 7 in order ─────────────────────── */

export const ResignationFormSections: React.FC<ResignationSectionProps & { startNumber?: number }> = (props) => {
  const { startNumber = 1 } = props;
  return (
    <>
      <SubjectEmployeeDetailsSection {...props} number={startNumber} />
      <ApprovalHierarchySection {...props} number={startNumber + 1} />
      <NoticePeriodSection {...props} number={startNumber + 2} />
      <EarlyRelievingSection {...props} number={startNumber + 3} />
      <BondContactDetailsSection {...props} number={startNumber + 4} />
      <ReasonAttachmentsSection {...props} number={startNumber + 5} />
      <DeclarationsHandoverSection {...props} number={startNumber + 6} />
    </>
  );
};
